import { Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PainelDashedAddCardButton } from '@/components/painel/cms/SmilieCodeField';
import { painelInputClass } from '@/components/painel/cms/painel-form';
import { stickerChatCode, stickerImageUrl } from '@/lib/xat-stickers';
import { cn } from '@/lib/utils';

type StickerCodesGridProps = {
  powerSlug: string;
  codes: string[];
  onChange: (codes: string[]) => void;
  emptyHint?: string;
};

function StickerThumb({ powerSlug, code }: { powerSlug: string; code: string }) {
  const [failed, setFailed] = useState(false);
  const name = code.trim().toLowerCase();
  const slug = powerSlug.trim().toLowerCase();

  if (!name || !slug || failed) {
    return (
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-white/[0.04] font-poppins text-[9px] text-slate-500">
        —
      </div>
    );
  }

  return (
    <img
      src={stickerImageUrl(slug, name)}
      alt={stickerChatCode(slug, name)}
      className="h-10 w-10 shrink-0 object-contain"
      loading="lazy"
      decoding="async"
      onError={() => setFailed(true)}
    />
  );
}

/** Grelha editável de stickers (pré-visualização CDN + código). */
export function StickerCodesGrid({
  powerSlug,
  codes,
  onChange,
  emptyHint,
}: StickerCodesGridProps) {
  const add = () => onChange([...codes, '']);
  const remove = (index: number) => onChange(codes.filter((_, i) => i !== index));
  const patch = (index: number, nextCode: string) => {
    const next = [...codes];
    next[index] = nextCode;
    onChange(next);
  };

  const slug = powerSlug.trim().toLowerCase();

  return (
    <div className="space-y-3">
      {codes.length === 0 ? (
        <>
          <p className="font-inter text-sm text-slate-400">
            {emptyHint ??
              'Nenhum sticker ainda. Os códigos entram pela API ao criar o power, ou adicione manualmente.'}
          </p>
          <PainelDashedAddCardButton
            label="Adicionar sticker"
            onClick={add}
            accent="pink"
            icon={<Plus className="h-6 w-6" />}
          />
        </>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {codes.map((code, index) => (
            <div
              key={index}
              className="flex min-h-[58px] items-center gap-2 rounded-xl border border-white/10 bg-white/[0.03] p-2"
            >
              <StickerThumb powerSlug={slug} code={code} />
              <div className="min-w-0 flex-1 space-y-1">
                <Input
                  value={code}
                  onChange={(e) => patch(index, e.target.value)}
                  className={cn(painelInputClass, 'min-w-0 font-mono text-xs')}
                  placeholder="pumpkin"
                />
                {slug && code.trim() ? (
                  <p className="truncate font-mono text-[10px] text-slate-500">
                    {stickerChatCode(slug, code)}
                  </p>
                ) : null}
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-9 w-9 shrink-0 rounded-xl text-rose-300 hover:bg-rose-500/15 hover:text-rose-100"
                onClick={() => remove(index)}
                aria-label={`Remover sticker ${index + 1}`}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}

          <PainelDashedAddCardButton
            label="Adicionar sticker"
            onClick={add}
            accent="pink"
            size="compact"
            icon={<Plus className="h-5 w-5" />}
          />
        </div>
      )}
    </div>
  );
}
